<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="deleteCustomerDialog"
            :action-loading="customerLoadingOnDelete"
            :data="customer"
            removal
            @on-confirm="deleteCustomer"
            @on-abort="closeDeleteCustomerDialog"
        >
            <template #title>
                Usuwanie klienta
            </template>
            <template #content>
                Czy na pewno chcesz usunąć klienta?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <page-title>Klienci</page-title>

        <v-row align="center">
            <v-col cols="12" sm="6" md="3" lg="4">
                <v-btn
                    :loading="customerLoading"
                    color="secondary"
                    medium
                    data-sentry-label="Dodaj klienta"
                    @click="createCustomer"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj klienta
                </v-btn>
            </v-col>
            <v-col cols="12" sm="12" md="9" lg="8">
                <v-container fluid class="pa-0">
                    <v-row dense justify="end">
                        <v-switch
                            v-model="showAll"
                            label="Pokaż wszystkich"
                            class="mr-4"
                            inset
                            @change="changeFilter"
                        />
                        <v-col cols="12" sm="12" md="4" lg="4">
                            <v-text-field
                                v-model="filters.nip"
                                append-icon="mdi-magnify"
                                label="Szukaj po NIP..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('nip')"
                            />
                        </v-col>
                        <v-col cols="12" sm="12" md="4" lg="4">
                            <v-text-field
                                v-model="filters.name"
                                append-icon="mdi-magnify"
                                label="Szukaj po nazwie"
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('name')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="customersHeaders"
                        :items="customers"
                        :loading="customersLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        name="customersData"
                        @pagination="updatePagination"
                    >
                        <template #item.customerType="{item}">
                            {{ item.customerType.name }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <template v-if="!item.isHidden">
                                    <v-icon color="primary" class="mr-2">
                                        mdi-eye
                                    </v-icon>
                                </template>
                                <template v-else>
                                    <v-icon color="grey" class="mr-2">
                                        mdi-eye-off
                                    </v-icon>
                                </template>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn icon data-sentry-label="Edytuj" v-on="on" @click="editCustomer(item.id)">
                                            <v-icon color="teal">
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edytuj</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Usuń"
                                            :disabled="item.isHidden"
                                            v-on="on"
                                            @click="showDeleteCustomerDialog(item.id)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import tooltipDelay from '@/mixins/tooltip'
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import { errorHandler } from '@/utils/errorHandler'
import dateFormatter from '@/mixins/dateFormatter'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'

export default {
    name: 'CustomersList',
    components: {
        PageTitle,
        ConfirmDialog
    },
    mixins: [
        dateFormatter,
        tooltipDelay,
        filters,
        pagination,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('customers/clearCustomersFilters')
            this.clearSelectedListItemId()
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            menuFrom: false,
            menuTo: false,
            customerLoading: false,
            deleteCustomerDialog: false,
            customer: {},
            showAll: false,
            customerLoadingOnDelete: false,
            customersHeaders: [
                {
                    text: 'Klient',
                    value: 'name',
                    sortable: false,
                },
                {
                    text: 'Nip',
                    value: 'nip',
                    sortable: false,
                },
                {
                    text: 'Typ klienta',
                    value: 'customerType',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ],
        }
    },
    computed: {
        customers() {
            return this.$store.state.customers.customers.customers
        },
        customersLoading() {
            return this.$store.state.customers.customers.customersLoading
        },
        savedFilters() {
            return this.$store.state.customers.customers.filters
        },
        selectedCustomerId() {
            return this.$store.state.common.selectedListItemId
        },
        filterQuery() {
            let filters = Object.assign({}, this.filters)
            let query = ''

            if (!this.showAll) {
                filters = Object.assign({ 'isHidden': 'false' }, this.filters)
            }
            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    if (item == 'createdAt__gte') {
                        filters[item] += 'T00:00:00Z'
                    }
                    if (item == 'createdAt__lte') {
                        filters[item] += 'T23:59:59Z'
                    }
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.getCustomers()
    },
    methods: {
        getCustomer(customerId) {
            this.$store.dispatch('customers/getCustomer', customerId)
                .then(response => {
                    this.customer = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getCustomers() {
            this.$store.dispatch('customers/getCustomers', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createCustomer() {
            this.customerLoading = true
            router.push({ name: 'customersCustomersCreate' })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.customerLoading = false
                })
        },
        editCustomer(customerId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(customerId)
            router.push({ name: 'customersCustomersEdit', params: { id: customerId } })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showDeleteCustomerDialog(customerId) {
            this.deleteCustomerDialog = true
            this.customer = this.getCustomer(customerId)
        },
        closeDeleteCustomerDialog() {
            this.deleteCustomerDialog = false
            this.getCustomers()
        },
        deleteCustomer(customer) {
            this.customerLoadingOnDelete = true
            this.$store.dispatch('customers/deleteCustomer', customer.id)
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeleteCustomerDialog()
                    this.customerLoadingOnDelete = false
                })
        },
        changeFilter() {
            this.$store.dispatch('customers/setCustomersFilters', this.filters)
                .catch(error => {
                    errorHandler(error)
                })
            this.resetPagination()
            this.getCustomers()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getCustomers()
            }
            this.clearPaginationPending()
        }
    }
}
</script>